export default function TenantOccupiedVsVacant() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-building-line"></i>
            <span>Occupancy Strategy</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Selling Tenant-Occupied vs. Vacant Units: Pros and Cons
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Understanding the advantages and disadvantages of each approach helps you make strategic decisions about timing and pricing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-3-line text-teal-600 text-3xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Tenant-Occupied Units</h3>
                <p className="text-slate-600 text-sm">Selling with tenants in place</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-teal-600 mb-4 flex items-center gap-2">
                <i className="ri-thumb-up-line"></i>
                Advantages
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-money-dollar-circle-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Immediate Cash Flow</p>
                    <p className="text-slate-600 text-xs">
                      Occupied units generate rental income right up to closing and continue producing income for the buyer immediately, making the property more attractive to investors.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-line-chart-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Higher Valuation</p>
                    <p className="text-slate-600 text-xs">
                      Properties with stable occupancy typically command 10-20% higher prices because buyers can immediately calculate NOI and cap rates based on actual rental income.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-shield-check-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Proven Income Stream</p>
                    <p className="text-slate-600 text-xs">
                      Existing leases demonstrate the property's ability to attract and retain tenants at current market rates, reducing buyer risk and uncertainty.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-time-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">No Vacancy Period</p>
                    <p className="text-slate-600 text-xs">
                      You avoid the carrying costs of vacant units (utilities, insurance, maintenance) while marketing the property for sale.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-bank-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Easier Buyer Financing</p>
                    <p className="text-slate-600 text-xs">
                      Lenders prefer occupied properties with documented income, making it easier for buyers to secure financing and close the deal.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-red-600 mb-4 flex items-center gap-2">
                <i className="ri-thumb-down-line"></i>
                Disadvantages
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-eye-off-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Limited Showing Access</p>
                    <p className="text-slate-600 text-xs">
                      Scheduling showings around tenant availability can be challenging and may limit buyer interest if units are difficult to view.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-home-2-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Unit Condition Concerns</p>
                    <p className="text-slate-600 text-xs">
                      Buyers see units "as lived in" which may not showcase the property's best features. Tenant housekeeping varies widely and can negatively impact buyer perception.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-user-unfollow-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Tenant Cooperation Issues</p>
                    <p className="text-slate-600 text-xs">
                      Some tenants may be uncooperative with showings, refuse access, or present the property poorly to potential buyers out of concern about new ownership.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-file-warning-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Lease Obligation Transfer</p>
                    <p className="text-slate-600 text-xs">
                      Buyer inherits all existing lease terms, including below-market rents, problem tenants, or unfavorable lease provisions you may have agreed to.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-door-open-line text-purple-600 text-3xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Vacant Units</h3>
                <p className="text-slate-600 text-sm">Selling with empty units</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-teal-600 mb-4 flex items-center gap-2">
                <i className="ri-thumb-up-line"></i>
                Advantages
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-calendar-check-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Flexible Showing Schedule</p>
                    <p className="text-slate-600 text-xs">
                      Show vacant units anytime without coordinating with tenants. Buyers can conduct thorough inspections and envision their own improvements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-paint-brush-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Renovation Opportunity</p>
                    <p className="text-slate-600 text-xs">
                      Vacant units can be renovated, staged, or improved before sale to showcase maximum potential and justify higher asking prices.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-sparkling-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Better Presentation</p>
                    <p className="text-slate-600 text-xs">
                      Clean, empty units allow buyers to see the space clearly without distractions from tenant belongings, odors, or maintenance issues.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-refresh-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Fresh Start for Buyer</p>
                    <p className="text-slate-600 text-xs">
                      Buyers can set their own rental rates, screen new tenants to their standards, and implement their management systems from day one.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <i className="ri-tools-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Easier Inspections</p>
                    <p className="text-slate-600 text-xs">
                      Property inspectors, contractors, and appraisers have unrestricted access to conduct thorough evaluations without tenant coordination.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-red-600 mb-4 flex items-center gap-2">
                <i className="ri-thumb-down-line"></i>
                Disadvantages
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-money-dollar-circle-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Lost Rental Income</p>
                    <p className="text-slate-600 text-xs">
                      Every vacant unit represents lost monthly income. A 12-unit building with 3 vacant units at $1,500/month loses $4,500 monthly or $54,000 annually.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-arrow-down-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Lower Property Valuation</p>
                    <p className="text-slate-600 text-xs">
                      Vacant units reduce NOI and cap rate calculations, potentially lowering the property value by 10-20% compared to fully occupied buildings.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-wallet-3-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Ongoing Carrying Costs</p>
                    <p className="text-slate-600 text-xs">
                      You continue paying utilities, insurance, property taxes, and maintenance on vacant units without offsetting rental income.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-question-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Market Rent Uncertainty</p>
                    <p className="text-slate-600 text-xs">
                      Without current tenants, buyers must estimate potential rental income, which introduces uncertainty and may result in lower offers.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
                  <i className="ri-shield-cross-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Security and Maintenance Risks</p>
                    <p className="text-slate-600 text-xs">
                      Vacant units are more vulnerable to vandalism, theft, and deterioration. You must maintain security and regular inspections.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mb-16">
          <div className="aspect-[21/9] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/09e5e496faa9594302eaf151b962977c.jpg"
              alt="Comparison of tenant-occupied versus vacant apartment units for sale"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Strategic Recommendations by Situation</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-star-line text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">High Occupancy (90%+)</h4>
              <p className="text-slate-700 text-sm mb-4">
                <strong>Recommendation:</strong> Sell with tenants in place
              </p>
              <p className="text-slate-600 text-sm">
                Strong occupancy demonstrates property performance and maximizes value. Focus on maintaining good tenant relationships and ensuring units show well during viewings.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-scales-line text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Mixed Occupancy (60-89%)</h4>
              <p className="text-slate-700 text-sm mb-4">
                <strong>Recommendation:</strong> Hybrid approach
              </p>
              <p className="text-slate-600 text-sm">
                Keep good tenants in place while renovating and staging vacant units. This showcases both current income and future potential to attract diverse buyer types.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-amber-600 text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Low Occupancy (&lt;60%)</h4>
              <p className="text-slate-700 text-sm mb-4">
                <strong>Recommendation:</strong> Consider value-add positioning
              </p>
              <p className="text-slate-600 text-sm">
                Market as a value-add opportunity. Renovate vacant units to show potential, but price reflects current low occupancy. Target investors seeking upside potential.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
